<template>
    <Teleport :to="headerMenuElement">
        <Transition name="fade">
            <InlineSwitcher
                v-if="switcher?.users"
                :switcher="switcher"
                size="small"
                @switch="switchUser"
            />
        </Transition>
    </Teleport>
    <SimpleWidget
        :widget-type="widgetType"
        :dependencies="dependencies"
        :placeholder-item-count="placeholderItemCount"
        :has-header="false"
        :loading="refreshing || loading"
        @retry="$emit('retry')"
    >
        <template #placeholder-item>
            <slot name="placeholder-item" />
        </template>
        <slot />
    </SimpleWidget>
</template>

<script setup>
    import { useWidgetsStore } from '@/stores/widgets'
    import { useWidgetPagesStore } from '@/stores/widgetPages'
    import InlineSwitcher from '@/components/ui/InlineSwitcher.vue'
    import SimpleWidget from '@/components/next-gen/widgets/shared/SimpleWidget.vue'

    const emit = defineEmits(['widgetPageIdChange', 'retry'])

    const props = defineProps({
        widgetType: String,
        dependencies: Array,
        widgetPageId: String,
        switcher: Object,
        loading: Boolean,
        placeholderItemCount: {
            type: Number,
            default: 5,
        },
    })

    const { snackbar } = useSnackbars()
    const widgetsStore = useWidgetsStore()
    const widgetPagesStore = useWidgetPagesStore()
    const widgetHelpers = useWidgetHelpers()

    const headerMenu = inject('headerMenu', null)

    const switching = ref(false)
    const refreshing = ref(false)

    const headerMenuElement = computed(() => {
        return headerMenu?.value?.$el?.querySelector('#header-menu')
    })

    const refreshingDependencies = (widgetId) => {
        if (widgetId !== props.widgetPageId) {
            return
        }
        refreshing.value = true
        widgetsStore.markDependenciesForRefresh(props.widgetType, props.dependencies)
    }

    const refreshedDependencies = (widgetId) => {
        if (widgetId !== props.widgetPageId) {
            return
        }
        widgetHelpers.fetchDependenciesHandlingRestartIfRequired(props.widgetType, props.dependencies)
            .finally(() => {
                refreshing.value = false
            })
    }

    const refreshDependenciesFailed = (widgetId) => {
        if (widgetId !== props.widgetPageId) {
            return
        }
        refreshing.value = false
    }

    // TODO: Clean up widgets that were loaded / switched
    const switchUser = (user) => {
        switching.value = true

        // TODO: Prevent new widget for same user URL
        widgetPagesStore.fetchWidgetPage(user.url)
            .then((id) => {
                emit('widgetPageIdChange', id)
            })
            .catch((e) => {
                snackbar({
                    message: 'Error switching',
                    type: 'error',
                })
            })
            .finally(() => {
                switching.value = false
            })
    }

    onMounted(() => {
        events.on('refreshing-widget-page', refreshingDependencies)
        events.on('refreshed-widget-page', refreshedDependencies)
        events.on('refresh-widget-page-failed', refreshDependenciesFailed)
    })

    onUnmounted(() => {
        events.off('refreshing-widget-page', refreshingDependencies)
        events.off('refreshed-widget-page', refreshedDependencies)
        events.off('refresh-widget-page-failed', refreshDependenciesFailed)
    })
</script>
